import React, { useState, useEffect } from "react";
import axios from "axios";
import { Checkbox, Spin, Empty, Collapse, Image, Typography, Upload, message } from "antd";
import {
  ClearOutlined,
  ReloadOutlined,
  CheckSquareOutlined,
  UploadOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem, dineroDisabledMask } from "../../../../../Components/Items";
import TextAreaItem from "../../../../../Components/TextAreaItem";
import SelectInsumosItem from "../../../../../Components/SelectInsumosItem";
import { formatearValorLabel } from "../../../../../Components/FiltroCategoriasInsumosItem";
import BarraFormato from "../BarraFormato";

const { Text } = Typography;
const { Panel } = Collapse;

// Campos de texto que se pueden incluir (orden de aparición, etiqueta, disponibilidad y línea).
const CAMPOS = [
  { key: "descripcion", label: "Descripción", disponible: (i) => !!i.descripcion, linea: (i) => i.descripcion },
  {
    key: "descripcionLarga",
    label: "Descripción larga / detalles",
    disponible: (i) => !!(i.descripcionLarga || i.detalles),
    linea: (i) => [i.descripcionLarga, i.detalles].filter(Boolean).join("\n"),
  },
  { key: "clave", label: "Clave", disponible: (i) => !!i.clave, linea: (i) => `Clave: ${i.clave}` },
  { key: "codigo", label: "Código", disponible: (i) => i.codigo != null, linea: (i) => `Código: ${i.codigo}` },
  {
    key: "codigoBarras",
    label: "Código de barras",
    disponible: (i) => !!i.codigoBarras,
    linea: (i) => `Código de barras: ${i.codigoBarras}`,
  },
  { key: "marca", label: "Marca", disponible: (i) => !!i.marca, linea: (i) => `Marca: ${i.marca}` },
  { key: "tipo", label: "Tipo de insumo", disponible: (i) => !!i.tipo, linea: (i) => `Tipo: ${i.tipo}` },
  { key: "familia", label: "Familia", disponible: (i) => !!i.familia, linea: (i) => `Familia: ${i.familia}` },
  { key: "unidad", label: "Unidad", disponible: (i) => !!i.unidad, linea: (i) => `Unidad: ${i.unidad}` },
];

const construirSel = (info, claves = []) => {
  const sel = {};
  CAMPOS.forEach((c) => {
    sel[c.key] = claves.includes(c.key) && c.disponible(info);
  });
  return sel;
};

const etiquetaCategoria = (c) => `${c.categoria}: ${formatearValorLabel(c.valor, c.tipoDato, c.unidad)}`;


const MARCA = { bold: "*", italic: "_", strike: "~" };
const aplicarMarca = (t, formato) => {
  const m = MARCA[formato];
  return m && t ? `${m}${t}${m}` : t;
};


const armarTexto = (info, sel, preciosSel, categoriasSel, formatos = {}) => {
  if (!info) return "";
  const lineas = [];

  CAMPOS.forEach((c) => {
    if (sel[c.key] && c.disponible(info)) lineas.push(aplicarMarca(c.linea(info), formatos[c.key]));
  });

  (info.categorias || []).forEach((c, idx) => {
    if (categoriasSel.includes(idx)) lineas.push(aplicarMarca(etiquetaCategoria(c), formatos.categorias));
  });

  (info.precios || [])
    .filter((p) => preciosSel.includes(p.nombre))
    .forEach((p) => lineas.push(aplicarMarca(`${p.nombre}: ${dineroDisabledMask(p.precio)}`, formatos.precios)));

  return lineas.join("\n");
};

const ComposerInsumo = ({ visible, headersToken, empresaId, puedeGuardarImagenInsumo, onCancel, onEnviar }) => {
  const [insumoId, setInsumoId] = useState(null);
  const [info, setInfo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [sel, setSel] = useState({});
  const [categoriasSel, setCategoriasSel] = useState([]);
  const [preciosSel, setPreciosSel] = useState([]);
  const [incluirImagen, setIncluirImagen] = useState(false);
  const [fichasSel, setFichasSel] = useState([]);
  const [imagenArchivo, setImagenArchivo] = useState(null); // imagen subida (sustituye a la del insumo)
  const [guardarEnInsumo, setGuardarEnInsumo] = useState(false);
  const [texto, setTexto] = useState("");
  const idTexto = "wa-composer-insumo-msg";

  useEffect(() => {
    setTexto(armarTexto(info, sel, preciosSel, categoriasSel, info?.formatosDefault || {}));
  }, [info, sel, preciosSel, categoriasSel]);

  const limpiar = () => {
    setInsumoId(null);
    setInfo(null);
    setCargando(false);
    setSel({});
    setCategoriasSel([]);
    setPreciosSel([]);
    setIncluirImagen(false);
    setFichasSel([]);
    setImagenArchivo(null);
    setGuardarEnInsumo(false);
    setTexto("");
  };

  const cerrar = () => {
    limpiar();
    onCancel();
  };

  // Al elegir un insumo, carga su info y prepara la selección por defecto.
  const onSelectInsumo = (id) => {
    setInsumoId(id);
    setInfo(null);
    if (!id) return;
    setCargando(true);
    axios
      .post("whatsapp/infoInsumo", { empresaId, insumoId: id }, { headers: headersToken })
      .then((r) => {
        if (r.data.success === 2) {
          const nuevaInfo = r.data.info;
          setInfo(nuevaInfo);
          aplicarPredeterminados(nuevaInfo);
        } else {
          message.error(r.data.message || "No se pudo cargar la información del insumo.");
        }
      })
      .catch(() => message.error("Error de red al cargar el insumo."))
      .finally(() => setCargando(false));
  };

  const aplicarPredeterminados = (datos) => {
    const info = datos || {};
    setSel(construirSel(info, info.camposDefault || []));
    setPreciosSel(
      (info.preciosDefault || []).filter((n) => (info.precios || []).some((p) => p.nombre === n)),
    );
    setCategoriasSel([]);
    setIncluirImagen(!!info.imagen && info.incluirImagenDefault !== false);
    setFichasSel([]);
    setImagenArchivo(null);
    setGuardarEnInsumo(false);
  };

  const borrarSeleccion = () => {
    setSel({});
    setPreciosSel([]);
    setCategoriasSel([]);
    setIncluirImagen(false);
    setFichasSel([]);
    setImagenArchivo(null);
    setGuardarEnInsumo(false);
  };

  const usarTodos = () => {
    if (!info) return;
    const todos = {};
    CAMPOS.forEach((c) => (todos[c.key] = c.disponible(info)));
    setSel(todos);
    setCategoriasSel((info.categorias || []).map((_, idx) => idx));
    setPreciosSel((info.precios || []).map((p) => p.nombre));
    setIncluirImagen(!!info.imagen);
  };

  const toggleCampo = (key) => setSel((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleEnArray = (setter, valor) =>
    setter((prev) => (prev.includes(valor) ? prev.filter((v) => v !== valor) : [...prev, valor]));

  const enviar = () => {
    if (!insumoId) return message.error("Selecciona un insumo.");
    const hayImagenInsumo = incluirImagen && !!info?.imagen;
    const hayImagen = !!imagenArchivo || hayImagenInsumo;
    const fichas = fichasSel.map((i) => info.fichas[i]).filter(Boolean);
    if (!texto.trim() && !hayImagen && !fichas.length) {
      return message.error("Selecciona al menos información, una imagen o una ficha técnica.");
    }
    onEnviar({
      tipo: "insumo",
      insumoId,
      texto: texto.trim(),
      incluirImagen: hayImagenInsumo, 
      imagenArchivo, 
      guardarEnInsumo: guardarEnInsumo && !!imagenArchivo,
      fichas,
    });
    limpiar();
  };

  const camposDisponibles = info ? CAMPOS.filter((c) => c.disponible(info)) : [];

  return (
    <ModalItem
      title="Detalle de insumo"
      visible={visible}
      onCancel={cerrar}
      width={600}
      zIndex={1100}
      destroyOnClose
      spinning={false}
    >
      <SelectInsumosItem
        headersToken={headersToken}
        empresaId={empresaId}
        placeholder="Buscar insumo..."
        width="100%"
        onChange={onSelectInsumo}
        dropdownMatchSelectWidth
        dropdownStyle={{ zIndex: 1200 }}
        placement="bottomLeft"
      />

      {cargando && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      )}

      {info && !cargando && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            <ButtonItem rojo icon={<ClearOutlined />} onClick={borrarSeleccion}>
              Borrar selección
            </ButtonItem>
            <ButtonItem turquesa icon={<ReloadOutlined />} onClick={() => aplicarPredeterminados(info)}>
              Predeterminados
            </ButtonItem>
            <ButtonItem lima icon={<CheckSquareOutlined />} onClick={usarTodos}>
              Usar todos
            </ButtonItem>
          </div>
          <Collapse defaultActiveKey={["info"]} style={{ marginTop: 12 }}>
            <Panel header="Información" key="info">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
                {camposDisponibles.map((c) => (
                  <Checkbox key={c.key} checked={!!sel[c.key]} onChange={() => toggleCampo(c.key)}>
                    {c.label}
                  </Checkbox>
                ))}
              </div>
            </Panel>

            {info.categorias?.length > 0 && (
              <Panel header={`Categorías especiales (${info.categorias.length})`} key="categorias">
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {info.categorias.map((c, idx) => (
                    <Checkbox
                      key={idx}
                      checked={categoriasSel.includes(idx)}
                      onChange={() => toggleEnArray(setCategoriasSel, idx)}
                    >
                      {etiquetaCategoria(c)}
                    </Checkbox>
                  ))}
                </div>
              </Panel>
            )}

            {info.precios?.length > 0 && (
              <Panel header="Precios" key="precios">
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
                  {info.precios.map((p) => (
                    <Checkbox
                      key={p.nombre}
                      checked={preciosSel.includes(p.nombre)}
                      onChange={() => toggleEnArray(setPreciosSel, p.nombre)}
                    >
                      {p.nombre}: {dineroDisabledMask(p.precio)}
                    </Checkbox>
                  ))}
                </div>
              </Panel>
            )}

            <Panel header="Adjuntos" key="adjuntos">
              {info.imagen && (
                <div style={{ marginBottom: 10 }}>
                  <Image
                    src={info.imagen.display}
                    alt="Imagen del insumo"
                    width={120}
                    style={{ borderRadius: 8, border: "1px solid #f0f0f0", background: "#fff" }}
                  />
                </div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Checkbox
                  checked={incluirImagen}
                  disabled={!info.imagen || !!imagenArchivo}
                  onChange={(e) => setIncluirImagen(e.target.checked)}
                >
                  Incluir imagen del insumo
                  {!info.imagen && <Text type="secondary"> (sin imagen)</Text>}
                  {!!imagenArchivo && <Text type="secondary"> (se usará la imagen subida)</Text>}
                </Checkbox>

                <div style={{ marginTop: 4 }}>
                  {imagenArchivo ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Image
                        src={URL.createObjectURL(imagenArchivo)}
                        alt="Imagen a enviar"
                        width={80}
                        style={{ borderRadius: 8, border: "1px solid #f0f0f0", background: "#fff" }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text style={{ fontSize: 12, display: "block", wordBreak: "break-all" }}>{imagenArchivo.name}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>Se enviará esta imagen en lugar de la del insumo.</Text>
                      </div>
                      <ButtonItem rojo icon={<DeleteOutlined />} onClick={() => { setImagenArchivo(null); setGuardarEnInsumo(false); }} />
                    </div>
                  ) : (
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(f) => { setImagenArchivo(f); setIncluirImagen(false); return false; }}
                    >
                      <ButtonItem turquesa icon={<UploadOutlined />}>Subir otra imagen para enviar</ButtonItem>
                    </Upload>
                  )}
                </div>

                {puedeGuardarImagenInsumo && imagenArchivo && (
                  <Checkbox
                    checked={guardarEnInsumo}
                    onChange={(e) => setGuardarEnInsumo(e.target.checked)}
                  >
                    Guardar esta imagen en el insumo
                  </Checkbox>
                )}

                {info.fichas?.map((f, idx) => (
                  <Checkbox
                    key={f.ruta}
                    checked={fichasSel.includes(idx)}
                    onChange={() => toggleEnArray(setFichasSel, idx)}
                  >
                    Ficha técnica: {f.nombre}
                  </Checkbox>
                ))}
              </div>
            </Panel>
          </Collapse>

          <div style={{ fontWeight: 600, margin: "14px 0 6px" }}>Mensaje</div>
          <BarraFormato targetId={idTexto} value={texto} onChange={setTexto} />
          <TextAreaItem
            id={idTexto}
            placeholder="El texto se arma con lo que selecciones. Puedes editarlo y dar formato (negrita/itálica/tachado)."
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={6}
          />
        </>
      )}

      {!info && !cargando && insumoId == null && (
        <Empty
          description="Busca y selecciona un insumo"
          style={{ padding: "24px 0 8px" }}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <ButtonItem rojo onClick={cerrar}>Cancelar</ButtonItem>
        <ButtonItem cyan onClick={enviar} disabled={!info}>Enviar</ButtonItem>
      </div>
    </ModalItem>
  );
};

export default ComposerInsumo;
